import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import EmptyState from "@/components/base/EmptyState";
import { FilterChipGroup, SelectField } from "@/components/base/Filters";
import MovieCard from "@/components/base/MovieCard";
import PageIntro from "@/components/base/PageIntro";
import usePageMeta from "@/hooks/usePageMeta";
import { movieItems } from "@/mocks/content";
import { formatViews } from "@/lib/format";

const INITIAL_COUNT = 6;
const LOAD_STEP = 6;

const mediaOptions = [
  { value: "all", label: "全部" },
  { value: "电影", label: "电影" },
  { value: "电视剧", label: "电视剧" },
];

const genreOptions = [
  { value: "剧情", label: "剧情" },
  { value: "悬疑", label: "悬疑" },
  { value: "爱情", label: "爱情" },
  { value: "科幻", label: "科幻" },
  { value: "青春", label: "青春" },
  { value: "都市", label: "都市" },
  { value: "喜剧", label: "喜剧" },
  { value: "运动", label: "运动" },
];

const regionOptions = [
  { value: "all", label: "全部地区" },
  { value: "中国", label: "中国" },
  { value: "日本", label: "日本" },
  { value: "韩国", label: "韩国" },
  { value: "法国", label: "法国" },
];

const yearOptions = [
  { value: "all", label: "全部年份" },
  { value: "2026", label: "2026" },
  { value: "2025", label: "2025" },
  { value: "2024", label: "2024 及更早" },
];

const sortOptions = [
  { value: "recommend", label: "推荐指数" },
  { value: "rating", label: "评分" },
  { value: "latest", label: "发布时间" },
];

export default function RecommendationsPage() {
  usePageMeta(
    "影视推荐 - 红桃网",
    "红桃网影视推荐频道，按电影与电视剧、类型、地区、年份筛选优质作品，提供导演、主演、评分与剧情简介，帮你快速找到下一部要看的片。",
    "影视推荐,电影推荐,电视剧推荐,影视片单,红桃网",
  );

  const [media, setMedia] = useState("all");
  const [genres, setGenres] = useState<string[]>([]);
  const [region, setRegion] = useState("all");
  const [year, setYear] = useState("all");
  const [sort, setSort] = useState("recommend");
  const [visible, setVisible] = useState(INITIAL_COUNT);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filtered = useMemo(() => {
    const list = movieItems.filter((movie) => {
      const mediaOk = media === "all" || movie.media === media;
      const genreOk = genres.length === 0 || genres.some((genre) => movie.genres.includes(genre));
      const regionOk = region === "all" || movie.region === region;
      const yearOk =
        year === "all" ||
        (year === "2024" ? movie.year <= 2024 : movie.year === Number(year));
      return mediaOk && genreOk && regionOk && yearOk;
    });

    return list.sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "latest") return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
      return b.recommendIndex - a.recommendIndex;
    });
  }, [media, genres, region, year, sort]);

  const visibleItems = filtered.slice(0, visible);

  const resetFilters = () => {
    setMedia("all");
    setGenres([]);
    setRegion("all");
    setYear("all");
    setSort("recommend");
    setVisible(INITIAL_COUNT);
  };

  const changeMedia = (value: string) => {
    setMedia(value);
    setVisible(INITIAL_COUNT);
  };

  const toggleGenre = (value: string) => {
    setGenres((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
    setVisible(INITIAL_COUNT);
  };

  const topRated = [...movieItems].sort((a, b) => b.rating - a.rating).slice(0, 5);
  const activeFilterCount =
    (media !== "all" ? 1 : 0) + genres.length + (region !== "all" ? 1 : 0) + (year !== "all" ? 1 : 0);

  return (
    <>
      <PageIntro
        kicker="影视片单"
        title="影视推荐"
        description="电影与剧集编辑片单。没有铺天盖地的热搜，只有我们认真看完之后，愿意推荐给你的作品。"
        breadcrumbs={[{ label: "首页", to: "/" }, { label: "影视推荐" }]}
        extra={
          <div className="flex items-center gap-5 text-sm">
            <div>
              <p className="font-label text-xl font-extrabold text-foreground-950">{movieItems.length}</p>
              <p className="text-xs text-foreground-500">部作品收录</p>
            </div>
            <span className="h-8 w-px bg-background-300" />
            <div>
              <p className="font-label text-xl font-extrabold text-primary-600">4</p>
              <p className="text-xs text-foreground-500">个地区片单</p>
            </div>
          </div>
        }
      />

      <div className="mx-auto w-full max-w-portal px-4 py-6 md:px-6 md:py-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-9">
          <div>
            <div className="rounded-lg border border-background-200 bg-background-50">
              <div className="flex items-center justify-between gap-3 border-b border-background-200 px-4 py-3">
                <div className="flex items-center gap-2">
                  <i className="ri-filter-3-line text-primary-500" />
                  <span className="font-heading text-sm font-semibold text-foreground-900">筛选条件</span>
                  {activeFilterCount > 0 && (
                    <span className="rounded-full bg-primary-500 px-1.5 py-0.5 font-label text-[10px] text-background-50">
                      {activeFilterCount}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setFiltersOpen((prev) => !prev)}
                  className="flex cursor-pointer items-center gap-1 text-xs text-foreground-500 md:hidden"
                >
                  {filtersOpen ? "收起" : "展开"}
                  <i className={filtersOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                </button>
              </div>

              <div className={`${filtersOpen ? "block" : "hidden"} space-y-3 px-4 py-4 md:block`}>
                <FilterChipGroup
                  label="作品类型"
                  options={mediaOptions}
                  selected={[media]}
                  onSelect={changeMedia}
                />
                <FilterChipGroup label="题材类型" options={genreOptions} selected={genres} onSelect={toggleGenre} />
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-1">
                  <SelectField
                    label="地区"
                    value={region}
                    options={regionOptions}
                    onChange={(value) => {
                      setRegion(value);
                      setVisible(INITIAL_COUNT);
                    }}
                  />
                  <SelectField
                    label="年份"
                    value={year}
                    options={yearOptions}
                    onChange={(value) => {
                      setYear(value);
                      setVisible(INITIAL_COUNT);
                    }}
                  />
                  <p className="text-[11px] text-foreground-400">题材类型支持多选，结果实时刷新。</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-background-200 px-4 py-3">
                <p className="text-xs text-foreground-500">
                  共 <span className="font-label font-semibold text-foreground-900">{filtered.length}</span> 部作品
                  {filtered.length > 0 && (
                    <span className="ml-1 hidden sm:inline">· 已展示 {Math.min(visible, filtered.length)} 部</span>
                  )}
                </p>
                <div className="flex items-center gap-1">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setSort(option.value);
                        setVisible(INITIAL_COUNT);
                      }}
                      className={[
                        "cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors",
                        sort === option.value
                          ? "bg-foreground-950 text-background-50"
                          : "text-foreground-600 hover:text-primary-600",
                      ].join(" ")}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {visibleItems.length > 0 ? (
              <>
                <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4">
                  {visibleItems.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>

                <div className="mt-6 flex flex-col items-center gap-3">
                  {visible < filtered.length ? (
                    <button
                      type="button"
                      onClick={() => setVisible((prev) => prev + LOAD_STEP)}
                      className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                    >
                      加载更多（还有 {filtered.length - visible} 部）
                      <i className="ri-arrow-down-line ml-1" />
                    </button>
                  ) : (
                    <p className="text-xs text-foreground-400">已经到底啦，共 {filtered.length} 部作品</p>
                  )}
                </div>
              </>
            ) : (
              <div className="mt-4">
                <EmptyState
                  title="没有符合条件的影视作品"
                  description="可以尝试切换作品类型，或减少题材、地区与年份的限制，也可以直接重置筛选条件。"
                  actionLabel="重置筛选"
                  onAction={resetFilters}
                  icon="ri-film-line"
                />
              </div>
            )}
          </div>

          <aside className="space-y-4 self-start">
            <div className="rounded-lg border border-background-200 bg-background-50">
              <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
                <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                  <i className="ri-trophy-line text-accent-600" />
                  评分榜
                </h2>
                <span className="font-label text-[10px] tracking-widest text-foreground-400">评分</span>
              </div>
              <ol className="divide-y divide-background-100 px-4">
                {topRated.map((movie, index) => (
                  <li key={movie.id}>
                    <Link to={`/movies/${movie.id}`} className="group flex items-center gap-3 py-3">
                      <span className="w-4 shrink-0 font-label text-xs font-bold text-foreground-400">
                        {index + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] text-foreground-800 transition-colors group-hover:text-primary-600 line-clamp-1">
                          {movie.title}
                        </span>
                        <span className="mt-0.5 block text-[11px] text-foreground-400">
                          {movie.media} · {movie.year}
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-1 font-label text-xs font-bold text-accent-700">
                        <i className="ri-star-fill text-[10px]" />
                        {movie.rating.toFixed(1)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                <i className="ri-eye-line text-primary-500" />
                最多人看
              </h2>
              <ul className="mt-3 space-y-2.5">
                {[...movieItems]
                  .sort((a, b) => b.views - a.views)
                  .slice(0, 4)
                  .map((movie) => (
                    <li key={movie.id}>
                      <Link
                        to={`/movies/${movie.id}`}
                        className="group flex items-center justify-between gap-3 text-[13px]"
                      >
                        <span className="truncate text-foreground-700 transition-colors group-hover:text-primary-600">
                          {movie.title}
                        </span>
                        <span className="shrink-0 font-label text-[11px] text-foreground-400">
                          {formatViews(movie.views)}
                        </span>
                      </Link>
                    </li>
                  ))}
              </ul>
            </div>

            <div className="rounded-lg bg-primary-950 p-4 text-background-100">
              <p className="font-label text-[11px] tracking-[0.22em] text-accent-400">小建议</p>
              <p className="mt-2 text-sm font-semibold text-background-50">不知道看什么？</p>
              <p className="mt-1.5 text-xs leading-relaxed text-primary-100/80">
                先按评分排序，再从最高的三部里挑一部。这个方法我们用了很多年，几乎没有失手。
              </p>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}